/** @jsxImportSource @emotion/react */

import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import { format } from 'date-fns'
import { FC, useState } from 'react'
import { VIDEO_DETAILS_DATE_FORMAT } from 'constants/video'
import { COURSE_COLORS, CourseName } from 'theme/utils'
import ConfirmationModal from 'components/Modals/ConfirmationModal'
import Badge from '../Badge'
import Tooltip from '../Tooltip'
import * as classes from './styles'

interface IProps {
  title: string
  itemName: string
  tag: CourseName
  tagLabel?: string
  createDate?: string
  statusLive: boolean
  onPublishClick?: () => void
  onDeleteClick?: () => void
  deleteDisabledReason?: string | null
  disabled?: boolean
  isPublishDisabled?: boolean
  errorText?: string | null
}

const DetailsBlock: FC<IProps> = ({
  statusLive,
  createDate,
  title,
  itemName,
  onPublishClick,
  onDeleteClick,
  deleteDisabledReason,
  tag,
  tagLabel,
  disabled,
  isPublishDisabled,
  errorText,
}) => {
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false)

  return (
    <div css={classes.container} data-testid="details-block">
      <div css={classes.containerBlock}>
        <Typography variant="body2" css={classes.title} data-testid="details-block-title">
          {title}
        </Typography>
        <div css={classes.infoBlock}>
          <Typography variant="body3" css={classes.infoTitle}>
            Tags:
          </Typography>
          <Badge
            text={tagLabel || tag}
            color={COURSE_COLORS[tag]?.main ?? '#ffffff'}
            data-testid="details-block-badge"
          />
        </div>
        <div css={classes.infoBlock}>
          <Typography variant="body3" css={classes.infoTitle}>
            Created date:
          </Typography>
          <Typography variant="body3" css={classes.info} data-testid="details-block-created-date">
            {createDate ? format(new Date(createDate), VIDEO_DETAILS_DATE_FORMAT) : '-'}
          </Typography>
        </div>
        <div css={classes.infoBlock}>
          <Typography variant="body3" css={classes.infoTitle}>
            Status:
          </Typography>
          <Typography variant="body3" css={classes.info} data-testid="details-block-status">
            {statusLive ? 'Live' : 'Not Live'}
          </Typography>
        </div>
      </div>
      <div css={classes.containerBlock}>
        <Typography variant="body2" css={classes.title}>
          Publish
        </Typography>
        <Button
          color="blue"
          size="medium"
          onClick={onPublishClick}
          fullWidth
          type="submit"
          disabled={disabled || isPublishDisabled}
          data-testid="details-block-publish-button"
        >
          Publish
        </Button>
        {createDate &&
          (deleteDisabledReason ? (
            <Tooltip title={deleteDisabledReason} isNote arrow>
              <Button
                variant="outlined"
                color="red"
                size="medium"
                fullWidth
                disabled
                data-testid="details-block-delete-button"
              >
                Delete
              </Button>
            </Tooltip>
          ) : (
            <Button
              variant="outlined"
              color="red"
              size="medium"
              onClick={() => setIsDeleteConfirmationOpen(true)}
              fullWidth
              disabled={!onDeleteClick || disabled}
              data-testid="details-block-delete-button"
            >
              Delete
            </Button>
          ))}
        {errorText && (
          <Typography variant="body2" css={classes.errorText} data-testid="details-block-error-text">
            {errorText}
          </Typography>
        )}
      </div>
      <ConfirmationModal
        open={isDeleteConfirmationOpen}
        title={`Deleting ${itemName}`}
        subtitle={`Are you sure you want to delete this ${itemName.toLowerCase()}? This action cannot be changed.`}
        onClose={() => setIsDeleteConfirmationOpen(false)}
        onConfirm={() => {
          setIsDeleteConfirmationOpen(false)
          onDeleteClick?.()
        }}
        confirmText="Delete"
        closeText="Cancel"
      />
    </div>
  )
}

export default DetailsBlock
